import { useEffect, useMemo } from "react"
import _ from "lodash"

import { useVenue } from "providers/venue"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { AmenityData, OccupantData, OpeningData } from "providers/venue/types"
import { useGeoLocation } from "providers/geolocation"

import {
  getLocationIdByFeature,
  getSuitablyValueBetweenBearings,
} from "components/IndoorMap"

/**
 * TODO:
 * - move `useHighLightFeaturesMap` to a separate folder (affects 17 files) ?
 * - move types to a separate file ?
 */
export type Feature = OccupantData | AmenityData | OpeningData

interface IOffset {
  top: number
  left: number
  right: number
  bottom: number
}

interface IHighlightOptions {
  offset?: IOffset
  enabled: boolean
}

type UseHighLightFeaturesMap = (
  features: Feature[],
  ordinal: number,
  options?: Partial<IHighlightOptions>
) => void

const DEFAULT_OPTIONS: IHighlightOptions = {
  offset: { top: 0, left: 0, right: 0, bottom: 0 },
  enabled: true,
}

export const useHighLightFeaturesMap: UseHighLightFeaturesMap = (
  features,
  ordinal,
  options = DEFAULT_OPTIONS
) => {
  const { indoorRef, mapLoaded } = useIndoorMap()
  const { dataLoaded, mapConfig } = useVenue()
  const { userLocation } = useGeoLocation()
  const { offset, enabled } = { ...DEFAULT_OPTIONS, ...options }
  const mapReady = enabled && indoorRef.current && mapLoaded && dataLoaded
  const { bottom, top, left, right } = offset

  const offsetOption = useMemo(
    () => ({ bottom, top, left, right }),
    [bottom, top, left, right]
  )

  useEffect(() => {
    if (mapReady) {
      const indoorMap = indoorRef.current
      indoorMap.clearAnimations()
      indoorMap.hideVenueObjects()
      indoorMap.clearNavigationGeometries()
      indoorMap.enableClick()
      indoorMap.setFeatureObject3DsOpacity(1)
      indoorMap.setSpriteMarkersOpacity(1)
    }
  }, [mapReady, indoorRef])

  useEffect(() => {
    const indoorMap = indoorRef.current
    if (mapReady && indoorMap) {
      // clear all highlight in current level before change ordinal
      indoorRef.current.clearHighlightElements()
      indoorRef.current.clearHighlightObject()
      indoorRef.current.changeLevelByOrdinal(ordinal)

      const featuresOnOrdinal = features.filter(
        (feature) => feature.properties.ordinal === ordinal
      )

      const filteredFeatureIds = featuresOnOrdinal.map((feature) => feature.id)
      const filteredLocationFeatureIds = featuresOnOrdinal.map((feature) =>
        getLocationIdByFeature(feature)
      )

      try {
        const currentBearing = indoorRef.current.getBearing()
        const defaultBearing = indoorRef.current.defaultBearing
        const newBearing = getSuitablyValueBetweenBearings(
          defaultBearing,
          currentBearing
        )
        const featureIdsToHighlight = _.compact([
          ...filteredFeatureIds,
          ...filteredLocationFeatureIds,
        ])
        const highlightedFeatures = indoorRef.current.setHighlightElementIds(
          featureIdsToHighlight
        )

        if (highlightedFeatures && !_.isEmpty(highlightedFeatures.features)) {
          // Handle Fly to animation
          userLocation &&
            userLocation.properties.ordinal === ordinal &&
            highlightedFeatures.features.push(userLocation)
          // Add space around highlighted features with `extentScaleFactor` to prevent markers from going out of frame.
          const extentScaleFactor = _.get(
            mapConfig,
            "extent_features_scale_factor",
            1
          )
          const extent = indoorRef.current.getFeatureExtent(
            highlightedFeatures,
            extentScaleFactor
          )
          const zoom = indoorRef.current.getExtentZoom(extent, {
            isFraction: true,
            padding: {
              paddingLeft: 64,
              paddingRight: 64,
              paddingTop: 64,
              paddingBottom: 64,
            },
          })
          const center = indoorRef.current.getExtentCenter(extent)
          const newCenter = indoorRef.current.getTargetViewCenter(
            {
              bearing: newBearing,
              center,
              pitch: 0,
              zoom: zoom - 0.5,
            },
            { offset: offsetOption }
          )
          indoorRef.current.flyTo(newCenter, {
            zoom,
          })
        } else {
          const defaultZoom = indoorRef.current.defaultZoom
          const defaultCenter = indoorRef.current.defaultCenter
          indoorRef.current.flyTo(defaultCenter, {
            zoom: defaultZoom,
            bearing: newBearing,
            pitch: 45,
          })
        }
      } catch (error) {
        console.log(error)
      }
    }
  }, [
    features,
    indoorRef,
    mapReady,
    ordinal,
    userLocation,
    offsetOption,
    mapConfig,
  ])
}
